/* eslint-disable @typescript-eslint/no-explicit-any */
import { Path } from 'react-hook-form'
import { Filters } from '../shared/shared-types'
import { ValidationConfig as TableValidationConfig } from '../DataTableComponent/types-v4'
import { BaseUISchema } from '../common-types'

export type ValidationMode = 'onBlur' | 'onChange' | 'onSubmit' | 'onTouched' | 'all'

// Conditional requireWhen support for form fields
export type RequireWhenOperator =
  | 'lt'
  | 'lte'
  | 'gt'
  | 'gte'
  | 'eq'
  | 'neq'
  | 'in'
  | 'notIn'
  | 'contains'
  | 'startsWith'
  | 'endsWith'
  | 'regex'

export type StatusColor = 'red' | 'amber' | 'green'

// Legacy numeric status ranges (deprecated). Prefer statusIndicator.
export interface LegacyStatusRange {
  lower: number
  upper: number
  color: StatusColor
}

export type RequireWhenCondition =
  | {
      operator: Exclude<RequireWhenOperator, 'in' | 'notIn'>
      value: string | number | boolean
    }
  | {
      operator: 'in' | 'notIn'
      value: Array<string | number | boolean>
    }

export type RequireWhenAnyOf = Record<string, RequireWhenCondition>

export interface RequireWhenRule {
  anyOf?: RequireWhenAnyOf
  allOf?: RequireWhenAnyOf
}

// Field-local status indicator rules (no cross-field deps). Conditions are evaluated against the field's own value.
export type ValueConditionOperator =
  | 'lt'
  | 'lte'
  | 'gt'
  | 'gte'
  | 'eq'
  | 'neq'
  | 'in'
  | 'notIn'
  | 'contains'
  | 'startsWith'
  | 'endsWith'
  | 'regex'

export type ValueCondition =
  | {
      operator: Exclude<ValueConditionOperator, 'in' | 'notIn' | 'regex'>
      value: string | number | boolean
    }
  | {
      operator: 'in' | 'notIn'
      value: Array<string | number | boolean>
    }
  | {
      operator: 'regex'
      value: string // regex pattern
    }

export interface StatusIndicatorRule {
  anyOf?: ValueCondition[]
  allOf?: ValueCondition[]
  color: StatusColor
}

export interface StatusIndicatorConfig {
  rules: StatusIndicatorRule[]
}

export interface FormValidationConfig extends TableValidationConfig {
  requireWhen?: RequireWhenRule
  // Unified message for requireWhen only
  messages?: (TableValidationConfig['messages'] & { requireWhen?: string }) | undefined
}
export interface JsonFormEnumOptionV2 {
  title: string
  value: string | number
  color?: string
}

// New interfaces for cascading field

export interface CascadingBaseOption {
  value: string
  label: string
}

export interface CascadingOption extends CascadingBaseOption {
  options?: CascadingOption[]
}

export interface CascadingCardIcon {
  icon: string
  colour?: string
}

export interface CascadingCard {
  title: string
  description: string
  primaryIcon: CascadingCardIcon
  secondaryIcon?: CascadingCardIcon
}

export interface CascadingCardOption extends CascadingBaseOption {
  card: CascadingCard
  options?: CascadingOption[]
}

export interface CascadingFieldLabels {
  level1?: string
  level2?: string
  level3?: string
  level4?: string
  level5?: string
  title?: string
}

export interface CascadingFieldMultiSelect {
  level1?: boolean
  level2?: boolean
  level3?: boolean
  level4?: boolean
  level5?: boolean
}

export interface CascadingFieldDefault {
  level1?: string
  level2?: string
  level3?: string | string[]
  level4?: string | string[]
  level5?: string | string[]
}

export interface JsonFormPropertyV2 {
  type: string
  title: string
  id: string
  description?: string
  documentation?: string
  format?: string
  required?: boolean
  minimum?: number
  maximum?: number
  minLength?: number
  maxLength?: number
  pattern?: string
  requireWhen?: RequireWhenRule
  validation?: FormValidationConfig
  choices?: (string | number)[] | JsonFormEnumOptionV2[]
  readOnly?: boolean
  readOnlyWhen?: RequireWhenRule
  visibleWhen?: RequireWhenRule
  items?: {
    type: string
    choices?: (string | number)[] | JsonFormEnumOptionV2[]
    widget?: 'range' | 'file' | 'multiselect' | 'textarea' | 'hidden'
    properties?: JsonFormPropertyV2[]
  }
  widget?: 'range' | 'file' | 'multiselect' | 'textarea' | 'hidden' | 'radio'
  // New properties for cascading field
  options?: CascadingOption[] | CascadingCardOption[]
  fieldLabels?: CascadingFieldLabels
  isMultiSelect?: CascadingFieldMultiSelect
  default?: any
  visible?: boolean
  statusIndicator?: StatusIndicatorConfig
  // Deprecated legacy numeric status ranges
  status?: LegacyStatusRange[]
  // New property for float field
  step?: number
}

export interface JsonFormSchemaV2 extends BaseUISchema {
  type: 'object'
  required?: string[]
  validationMode?: ValidationMode
  properties: JsonFormPropertyV2[]
}

export interface BaseFieldProps {
  field: JsonFormPropertyV2
  formFieldName: Path<FormValues>
  control: any
  variant?: 'row' | 'column' | 'inline'
}

type InferFormPropertyValue<T extends JsonFormPropertyV2> = T extends {
  type: 'string'
  format: 'date' | 'date-time'
}
  ? string
  : T extends { type: 'string'; format: 'data-url' }
    ? File
    : T extends { type: 'string'; choices: readonly string[] }
      ? T['choices'][number]
      : T extends { type: 'string' }
        ? string
        : T extends { type: 'integer' | 'number' }
          ? number
          : T extends { type: 'boolean' }
            ? boolean
            : T extends { type: 'array'; items: { choices: readonly (string | number)[] } }
              ? Array<T['items']['choices'][number]>
              : T extends { type: 'cascading' }
                ? {
                    level1?: string
                    level2?: string | string[]
                    level3?: string | string[]
                    level4?: string | string[]
                    level5?: string | string[]
                  }
                : unknown

export type InferFormValues<T extends JsonFormSchemaV2> = {
  [Property in T['properties'][number] as Property['id']]: InferFormPropertyValue<Property>
}

export interface UISchema {
  actions: {
    handlerId: string
  }
  schema: JsonFormSchemaV2
}

export interface FormResponse {
  uiSchema: UISchema
  // dataSchema: {}
  filters: Filters
}

export interface FormNavigation {
  pageId: string
  filters?: InferFormValues<JsonFormSchemaV2>
}

/** How `content` should be interpreted before building the download Blob. */
export type FileDownloadContentEncoding = 'utf-8' | 'base64'

export interface FileDownload {
  filename: string
  content: string
  mediaType?: string
  /** Default utf-8: text download. base64: decode `content` to binary data. */
  contentEncoding?: FileDownloadContentEncoding
}

export interface FormSubmitResponse {
  uiSchema: {
    contentToRefresh?: string[]
    navigation?: FormNavigation
    message?: string
    success?: boolean
    showToast?: boolean
    fileDownload?: FileDownload
  }
}

// Define the FormValues type based on the schema
export type FormValues = InferFormValues<JsonFormSchemaV2>

// Type guards for CascadingOption and CascadingCardOption
export function isCascadingOptionArray(options: unknown): options is CascadingOption[] {
  if (!Array.isArray(options)) return false
  // CascadingOption has 'value' and 'label', but not 'card'
  return options.every(
    (opt) => typeof opt === 'object' && opt !== null && 'value' in opt && 'label' in opt && !('card' in opt)
  )
}

export function isCascadingCardOptionArray(options: unknown): options is CascadingCardOption[] {
  if (!Array.isArray(options)) return false
  // CascadingCardOption has 'value', 'label', and 'card'
  return options.every(
    (opt) =>
      typeof opt === 'object' &&
      opt !== null &&
      'value' in opt &&
      'label' in opt &&
      'card' in opt &&
      typeof opt.card === 'object' &&
      opt.card !== null &&
      'title' in opt.card &&
      'description' in opt.card &&
      'primaryIcon' in opt.card &&
      'secondaryIcon' in opt.card
  )
}
